import { useTranslation } from "react-i18next";
import { ColorDot } from "~/components/charts";
import { Text } from "~/components/text";
import { formatCurrency, formatNumber } from "~/utils/formatting";
import type { SalesChartDatum } from "~/pages/product-details/tabs/sales-data-tab/data/map-sales-response";
import { FormatMatrixTooltip } from "~/pages/product-details/tabs/sales-data-tab/components/format-matrix-tooltip";
import type { TotalSalesTooltipSeries } from "./total-sales-tooltip";

export interface TotalSalesFormatTooltipProps {
  formats: TotalSalesTooltipSeries[];
  markets: TotalSalesTooltipSeries[];
  active?: boolean;
  payload?: { payload: SalesChartDatum }[];
}

const MarketLabel = ({ market }: { market: TotalSalesTooltipSeries }) => (
  <Text
    as="span"
    variant="tooltip"
    className="flex items-center gap-1.5 text-white/60"
  >
    <ColorDot
      shape="round"
      className={market.colorClassName}
      style={
        market.colorClassName ? undefined : { backgroundColor: market.color }
      }
    />
    {market.label}
  </Text>
);

export const TotalSalesFormatTooltip = ({
  formats,
  markets,
  active,
  payload,
}: TotalSalesFormatTooltipProps) => {
  const { t } = useTranslation(["productDetails"]);

  if (!active || !payload?.length || !formats.length) {
    return null;
  }

  const datum = payload[0].payload;
  const detailByKey = datum.detail ?? {};

  const marketHasUnits = (marketKey: string) =>
    formats.some(
      (format) =>
        (detailByKey[format.key]?.byMarket?.[marketKey]?.units ?? 0) > 0,
    );

  const marketHasRevenue = (marketKey: string) =>
    formats.some(
      (format) =>
        detailByKey[format.key]?.byMarket?.[marketKey]?.revenue != null,
    );

  return (
    <FormatMatrixTooltip date={datum.date} columns={formats}>
      <Text as="span" variant="tooltip" className="text-white/60">
        {t("productDetails:salesData.totalSales.tooltip.units")}
      </Text>
      {formats.map((format) => (
        <div key={format.key} className="text-right font-semibold">
          <Text as="span" variant="tooltip" numeric="mono">
            {formatNumber(detailByKey[format.key]?.units ?? 0)}
          </Text>
        </div>
      ))}

      {markets.map((market) => {
        if (!marketHasUnits(market.key)) {
          return null;
        }
        return (
          <div key={`units-${market.key}`} className="contents">
            <MarketLabel market={market} />
            {formats.map((format) => {
              const units =
                detailByKey[format.key]?.byMarket?.[market.key]?.units ?? 0;
              return (
                <Text
                  key={format.key}
                  as="span"
                  variant="tooltip"
                  numeric="mono"
                  className="text-right"
                >
                  {units ? formatNumber(units) : "—"}
                </Text>
              );
            })}
          </div>
        );
      })}

      <div className="col-span-full border-t border-white/10 pt-1.5">
        {/* Same variant as Units; colour muted for hierarchy. */}
        <Text as="span" variant="tooltip" className="text-white/50">
          {t("productDetails:salesData.totalSales.tooltip.salesByMarket")}
        </Text>
      </div>

      {markets.map((market) => {
        if (!marketHasRevenue(market.key)) {
          return null;
        }
        return (
          <div key={`revenue-${market.key}`} className="contents">
            <MarketLabel market={market} />
            {formats.map((format) => {
              const entry = detailByKey[format.key]?.byMarket?.[market.key];
              return (
                <Text
                  key={format.key}
                  as="span"
                  variant="tooltip"
                  numeric="mono"
                  className="text-right"
                >
                  {entry?.revenue != null
                    ? formatCurrency(entry.revenue, entry.currency)
                    : "—"}
                </Text>
              );
            })}
          </div>
        );
      })}
    </FormatMatrixTooltip>
  );
};
